// React
import { memo, useCallback, useEffect, useState } from "react";

// Tipovi
import { InvoiceIncomingRejection, InvoiceIncomingRejectionReasonType } from "services/api/finance/incoming-invoices/types";

// UI
import { Button } from "ui/components/Button/Button";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { TextArea } from "ui/components/TextField/TextArea";

import { REJECTION_REASON_TYPE_OPTIONS } from "./consts";
import { InvoiceIncomingRejectModalProps } from "./types";

/**
 * Odbijanje e-računa se prijavljuje provideru i fiskalizira kod Porezne uprave, pa razlog nije
 * formalnost — bez tipa i opisa backend odbija zahtjev.
 */
const InvoiceIncomingRejectModal: React.FC<InvoiceIncomingRejectModalProps> = (props) => {
  const { show, onHide, onConfirm, invoiceNumber } = props;

  const [reasonType, setReasonType] = useState<InvoiceIncomingRejectionReasonType | null>(null);
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!show) return;

    setReasonType(null);
    setReason("");
    setError("");
  }, [show]);

  const handleReasonTypeChange = useCallback((_event: any, _name: string, item: DropdownOption) => {
    setReasonType(item?.value ? (item.value as InvoiceIncomingRejectionReasonType) : null);
    setError("");
  }, []);

  const handleReasonChange = useCallback((event: any) => {
    setReason(event.target.value);
    setError("");
  }, []);

  const handleConfirm = useCallback(() => {
    if (!reasonType || !reason.trim()) {
      setError("Odaberite tip razloga i upišite obrazloženje.");
      return;
    }

    const rejection: InvoiceIncomingRejection = { reasonType, reason: reason.trim() };
    onConfirm(rejection);
  }, [reasonType, reason, onConfirm]);

  return (
    <Modal show={show} onHide={onHide} position="centered" size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={`Odbijanje računa ${invoiceNumber ?? ""}`.trim()}
        onHide={onHide}
      />
      <ModalBody>
        <div className="p-4 space-y-4">
          <div className="text-sm text-slate-500">
            Odbijanje se prijavljuje dobavljačevom provideru i fiskalizira kod Porezne uprave, pa se
            naknadno ne može povući.
          </div>

          <Dropdown
            name="reasonType"
            label="Tip razloga"
            options={REJECTION_REASON_TYPE_OPTIONS}
            value={reasonType}
            onChange={handleReasonTypeChange}
            placeholder="Odaberite tip razloga"
            required
          />

          <TextArea
            name="reason"
            label="Obrazloženje"
            placeholder="Zašto se račun odbija..."
            value={reason}
            onChange={handleReasonChange}
            rows={4}
            required
          />

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outlined" onClick={onHide}>
              Odustani
            </Button>
            <Button variant="remove" onClick={handleConfirm}>
              Odbij račun
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default memo(InvoiceIncomingRejectModal);
